import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MessageSquare } from 'lucide-react';

export const SMSTermsPage = () => {
  const lastUpdated = 'January 2026';

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="sms-terms-page">
      {/* Header */}
      <section className="py-12 sm:py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase mb-4">
              SMS Terms / <span className="text-amber-500">Mobile Terms of Service</span>
            </h1>
            <p className="text-slate-300">
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-none">
            {/* Program Info */}
            <div className="bg-slate-900 border border-amber-500/30 p-6 sm:p-8 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <MessageSquare className="w-6 h-6 text-amber-500" />
                <h2 className="font-heading font-bold text-2xl text-white uppercase m-0">
                  Pro Painting Alerts
                </h2>
              </div>
              <p className="text-slate-300 m-0">
                These terms govern your participation in the Pro Painting SMS/text messaging program.
              </p>
            </div>

            <div className="space-y-8">
              {/* Program Description */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Program Description
                </h2>
                <p className="text-slate-300 mb-4">
                  Pro Painting Alerts is our SMS communication program. Messages may include:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Appointment confirmations and reminders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Estimate updates and notifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Project status communications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Service-related updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Promotional offers (only if you opt in to marketing messages)</span>
                  </li>
                </ul>
              </div>

              {/* Opt-In */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Opt-In
                </h2>
                <p className="text-slate-300 mb-4">
                  You may opt in to receive SMS messages from Pro Painting by:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Submitting a form on our website and checking the SMS consent checkbox</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Verbally requesting text communications during a phone call</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Otherwise explicitly requesting text message communications</span>
                  </li>
                </ul>
                <p className="text-slate-300 mt-4 font-semibold">
                  Consent is not required as a condition of purchasing any goods or services.
                </p>
              </div>

              {/* Message Frequency */}
              <div className="bg-slate-900 border border-slate-800 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Message Frequency
                </h2>
                <p className="text-slate-300">
                  Message frequency may vary based on your interactions with Pro Painting, including 
                  appointment scheduling, estimate requests, and project status. You may receive 
                  multiple messages during active projects.
                </p>
              </div>

              {/* Rates */}
              <div className="bg-slate-900 border border-slate-800 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Message and Data Rates
                </h2>
                <p className="text-slate-300">
                  <strong className="text-white">Message and data rates may apply.</strong> Depending 
                  on your mobile carrier plan, you may be charged for each text message sent or 
                  received. Please contact your mobile carrier for details about your text messaging plan.
                </p>
              </div>

              {/* Opt-Out */}
              <div className="bg-cyan-500/10 border border-cyan-500/30 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Opt-Out Instructions
                </h2>
                <p className="text-slate-300 mb-4">
                  You can opt out of receiving SMS messages at any time by:
                </p>
                <div className="bg-slate-950 border border-slate-800 p-4 mb-4">
                  <p className="text-white text-lg font-bold text-center">
                    Reply <span className="text-amber-500">STOP</span> to cancel
                  </p>
                </div>
                <p className="text-slate-300">
                  After opting out, you will receive one final confirmation message. You will no longer 
                  receive SMS messages from Pro Painting unless you opt in again.
                </p>
              </div>

              {/* Help */}
              <div className="bg-slate-900 border border-slate-800 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Help
                </h2>
                <p className="text-slate-300 mb-4">
                  For help with SMS messaging:
                </p>
                <div className="bg-slate-950 border border-slate-800 p-4 mb-4">
                  <p className="text-white text-lg font-bold text-center">
                    Reply <span className="text-cyan-500">HELP</span> for assistance
                  </p>
                </div>
                <p className="text-slate-300 mb-4">
                  Or contact us directly:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-slate-400">
                    <Phone className="w-5 h-5 text-amber-500" />
                    <a href="tel:+18303270136" className="hover:text-amber-500 transition-colors">
                      (830) 327-0136
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-slate-400">
                    <Mail className="w-5 h-5 text-amber-500" />
                    <a href="mailto:matthew@propainting.cloud" className="hover:text-amber-500 transition-colors">
                      matthew@propainting.cloud
                    </a>
                  </div>
                </div>
              </div>

              {/* Privacy */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Privacy
                </h2>
                <p className="text-slate-300 mb-4">
                  Your privacy is important to us. Please review our full{' '}
                  <Link to="/privacy-225682" className="text-amber-500 hover:text-amber-400">
                    Privacy Policy
                  </Link>{' '}
                  for complete information about how we collect, use, and protect your information.
                </p>
                <p className="text-slate-300 font-semibold">
                  We do not share your mobile information with third parties or affiliates for 
                  marketing or promotional purposes.
                </p>
              </div>

              {/* Carrier Disclaimer */}
              <div className="bg-slate-900 border border-slate-800 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Carrier Disclaimer
                </h2>
                <p className="text-slate-300">
                  Mobile carriers (including but not limited to AT&T, Verizon, T-Mobile, Sprint, 
                  and others) are not liable for delayed or undelivered messages. Message delivery 
                  is subject to effective transmission from your network operator and may be affected 
                  by network conditions, device capabilities, and other factors outside our control.
                </p>
              </div>

              {/* Supported Carriers */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Supported Carriers
                </h2>
                <p className="text-slate-300">
                  Our SMS program is supported on most major U.S. carriers including AT&T, Verizon, 
                  T-Mobile, Sprint, and most regional carriers. Carriers are not responsible for 
                  delayed or undelivered messages.
                </p>
              </div>

              {/* Contact Information */}
              <div className="bg-slate-900 border border-amber-500/30 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Contact Us
                </h2>
                <p className="text-slate-300 mb-4">
                  If you have any questions about our SMS program:
                </p>
                <div className="space-y-3">
                  <p className="text-white font-bold">Pro Painting</p>
                  <div className="flex items-center gap-3 text-slate-400">
                    <Phone className="w-5 h-5 text-amber-500" />
                    <a href="tel:+18303270136" className="hover:text-amber-500 transition-colors">
                      (830) 327-0136
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-slate-400">
                    <Mail className="w-5 h-5 text-amber-500" />
                    <a href="mailto:matthew@propainting.cloud" className="hover:text-amber-500 transition-colors">
                      matthew@propainting.cloud
                    </a>
                  </div>
                </div>
              </div>

              {/* Related Links */}
              <div className="flex flex-wrap gap-4 pt-6 border-t border-slate-800">
                <Link 
                  to="/privacy-225682" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link 
                  to="/terms" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Terms of Service
                </Link>
                <Link 
                  to="/contact" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SMSTermsPage;
